(() => {
  'use strict';
  if (window.__ROCKY_MOTION_COMMERCE_RK4__) return;
  window.__ROCKY_MOTION_COMMERCE_RK4__ = true;

  document.body.classList.add('rk4-site');
  const template = document.querySelector('main')?.dataset.template || '';
  if (template.startsWith('product')) document.body.classList.add('rk4-product-page');

  const $ = (selector, root = document) => root?.querySelector(selector) || null;
  const $$ = (selector, root = document) => root ? [...root.querySelectorAll(selector)] : [];
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let toastTimer = 0;

  const showToast = message => {
    const toast = $('[data-rk4-toast]');
    if (!toast || !message) return;
    toast.textContent = message;
    toast.classList.add('is-visible');
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 2400);
  };

  const syncBodyLock = () => {
    const menuOpen = $('[data-rk4-mobile-menu]')?.classList.contains('is-open');
    const viewerOpen = Boolean($('[data-rk4-viewer]')?.open);
    document.body.classList.toggle('rk4-locked', Boolean(menuOpen || viewerOpen));
  };

  const initHeader = root => {
    const wrap = $('[data-rk4-header-wrap]', root) || $('[data-rk4-header-wrap]');
    if (!wrap || wrap.dataset.rk4Ready === 'true') return;
    wrap.dataset.rk4Ready = 'true';
    const alwaysSolid = wrap.dataset.alwaysSolid === 'true';
    const updateHeader = () => wrap.classList.toggle('is-solid', alwaysSolid || window.scrollY > 18);
    updateHeader();
    window.addEventListener('scroll', updateHeader, { passive: true });

    const mega = $('[data-rk4-mega]', wrap);
    const megaButton = $('[data-rk4-mega-open]', wrap);
    const scrim = $('[data-rk4-scrim]');
    const closeMega = () => {
      mega?.classList.remove('is-open');
      megaButton?.setAttribute('aria-expanded', 'false');
      scrim?.classList.remove('is-visible');
    };
    megaButton?.addEventListener('click', event => {
      event.preventDefault();
      const shouldOpen = !mega?.classList.contains('is-open');
      closeMega();
      if (shouldOpen) {
        mega?.classList.add('is-open');
        megaButton.setAttribute('aria-expanded', 'true');
        scrim?.classList.add('is-visible');
      }
    });
    scrim?.addEventListener('click', closeMega);
    document.addEventListener('keydown', event => { if (event.key === 'Escape') closeMega(); });
  };

  const initMobileMenu = root => {
    const menu = $('[data-rk4-mobile-menu]');
    const openers = $$('[data-rk4-mobile-open]', root);
    if (!menu || !openers.length || menu.dataset.rk4Ready === 'true') return;
    menu.dataset.rk4Ready = 'true';
    let lastTrigger = null;

    const open = trigger => {
      lastTrigger = trigger || null;
      menu.classList.add('is-open');
      menu.setAttribute('aria-hidden', 'false');
      openers.forEach(button => button.setAttribute('aria-expanded', 'true'));
      syncBodyLock();
      const video = $('video', menu);
      video?.play?.().catch(() => {});
      requestAnimationFrame(() => $('[data-rk4-mobile-close]', menu)?.focus({ preventScroll: true }));
    };
    const close = restoreFocus => {
      menu.classList.remove('is-open');
      menu.setAttribute('aria-hidden', 'true');
      openers.forEach(button => button.setAttribute('aria-expanded', 'false'));
      $('video', menu)?.pause?.();
      syncBodyLock();
      if (restoreFocus && lastTrigger) requestAnimationFrame(() => lastTrigger.focus({ preventScroll: true }));
    };

    openers.forEach(button => button.addEventListener('click', event => {
      event.preventDefault();
      open(button);
    }));
    $('[data-rk4-mobile-close]', menu)?.addEventListener('click', () => close(true));
    menu.addEventListener('click', event => {
      if (event.target === menu) close(false);
      if (event.target.closest('[data-rk4-mobile-search]')) {
        event.preventDefault();
        close(false);
        requestAnimationFrame(() => document.querySelector('search-button button')?.click());
      }
      if (event.target.closest('a')) close(false);
    });
    const groups = $$('details', menu);
    groups.forEach(detail => detail.addEventListener('toggle', () => {
      if (!detail.open) return;
      groups.forEach(other => { if (other !== detail) other.open = false; });
    }));
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape' && menu.classList.contains('is-open')) close(true);
    });
  };

  const initTabs = root => {
    $$('[data-rk4-tabs]', root).forEach(tabList => {
      if (tabList.dataset.rk4Ready === 'true') return;
      tabList.dataset.rk4Ready = 'true';
      const section = tabList.closest('[data-rk4-tab-section]');
      if (!section) return;
      const tabs = $$('[role="tab"]', tabList);
      const panels = $$('[role="tabpanel"]', section);
      const activate = (tab, focus = false) => {
        const id = tab.getAttribute('aria-controls');
        tabs.forEach(item => {
          const active = item === tab;
          item.setAttribute('aria-selected', String(active));
          item.tabIndex = active ? 0 : -1;
        });
        panels.forEach(panel => { panel.hidden = panel.id !== id; });
        if (focus) tab.focus();
      };
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(tab));
        tab.addEventListener('keydown', event => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let next = index;
          if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
          if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = tabs.length - 1;
          activate(tabs[next], true);
        });
      });
    });
  };

  const initReveal = root => {
    const items = $$('[data-rk4-reveal]', root).filter(item => item.dataset.rk4Observed !== 'true');
    if (!items.length) return;
    if (reduceMotion() || !('IntersectionObserver' in window)) {
      items.forEach(item => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries, instance) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        instance.unobserve(entry.target);
      });
    }, { threshold: .1, rootMargin: '0px 0px -5% 0px' });
    items.forEach(item => {
      item.dataset.rk4Observed = 'true';
      observer.observe(item);
    });
  };

  const initVideos = root => {
    const videos = $$('.rk4-autoplay', root).filter(video => video.dataset.rk4Observed !== 'true');
    if (!videos.length) return;
    videos.forEach(video => {
      video.dataset.rk4Observed = 'true';
      video.muted = true;
      video.playsInline = true;
    });
    if (reduceMotion() || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(entries => entries.forEach(entry => {
      const video = entry.target;
      if (entry.isIntersecting) video.play().catch(() => {});
      else video.pause();
    }), { threshold: .22 });
    videos.forEach(video => observer.observe(video));
  };

  const initViewer = root => {
    $$('[data-rk4-reels-section]', root).forEach(section => {
      if (section.dataset.rk4Ready === 'true') return;
      section.dataset.rk4Ready = 'true';
      const cards = $$('[data-rk4-reel-card]', section);
      const viewer = $('[data-rk4-viewer]', section);
      if (!cards.length || !viewer) return;
      const mediaHost = $('[data-rk4-viewer-media]', viewer);
      const progressHost = $('[data-rk4-viewer-progress]', viewer);
      let index = 0;
      let animationFrame = 0;
      let startedAt = 0;
      let lastTrigger = null;
      const duration = 7200;

      const stop = () => {
        window.cancelAnimationFrame(animationFrame);
        mediaHost?.querySelector('video')?.pause();
      };
      const progress = value => {
        $$('span', progressHost).forEach((bar, barIndex) => {
          const fill = $('i', bar);
          if (!fill) return;
          fill.style.width = `${barIndex < index ? 100 : barIndex > index ? 0 : value}%`;
        });
      };
      const tick = timestamp => {
        if (!viewer.open || reduceMotion()) return;
        if (!startedAt) startedAt = timestamp;
        const value = Math.min(100, ((timestamp - startedAt) / duration) * 100);
        progress(value);
        if (value >= 100) {
          if (index < cards.length - 1) render(index + 1);
          else close();
          return;
        }
        animationFrame = requestAnimationFrame(tick);
      };
      const renderMedia = card => {
        mediaHost.replaceChildren();
        const videoUrl = card.dataset.video || '';
        const imageUrl = card.dataset.image || '';
        if (videoUrl) {
          const video = document.createElement('video');
          video.src = videoUrl;
          video.poster = imageUrl;
          video.autoplay = true;
          video.muted = true;
          video.loop = true;
          video.playsInline = true;
          video.preload = 'metadata';
          mediaHost.append(video);
          video.play().catch(() => {});
        } else if (imageUrl) {
          const image = document.createElement('img');
          image.src = imageUrl;
          image.alt = card.dataset.title || '';
          image.decoding = 'async';
          mediaHost.append(image);
        }
      };
      const render = next => {
        stop();
        index = (next + cards.length) % cards.length;
        const card = cards[index];
        renderMedia(card);
        $('[data-rk4-viewer-count]', viewer).textContent = `${index + 1} / ${cards.length}`;
        $('[data-rk4-viewer-eyebrow]', viewer).textContent = card.dataset.eyebrow || 'ROCKY IN MOTION';
        $('[data-rk4-viewer-title]', viewer).textContent = card.dataset.title || '';
        $('[data-rk4-viewer-copy]', viewer).textContent = card.dataset.copy || '';
        $('[data-rk4-viewer-price]', viewer).textContent = card.dataset.price || '';
        const productImage = $('[data-rk4-viewer-product-image]', viewer);
        if (productImage) {
          productImage.src = card.dataset.productImage || card.dataset.image || '';
          productImage.alt = card.dataset.title || '';
        }
        const link = $('[data-rk4-viewer-link]', viewer);
        if (link) link.href = card.dataset.url || '#';
        startedAt = 0;
        progress(0);
        if (!reduceMotion()) animationFrame = requestAnimationFrame(tick);
      };
      const open = trigger => {
        lastTrigger = trigger;
        index = Math.max(0, cards.indexOf(trigger.closest('[data-rk4-reel-card]')));
        progressHost.replaceChildren(...cards.map(() => {
          const bar = document.createElement('span');
          bar.append(document.createElement('i'));
          return bar;
        }));
        if (!viewer.open) viewer.showModal();
        syncBodyLock();
        render(index);
        requestAnimationFrame(() => $('[data-rk4-viewer-close]', viewer)?.focus({ preventScroll: true }));
      };
      const close = () => {
        stop();
        mediaHost.replaceChildren();
        if (viewer.open) viewer.close();
        syncBodyLock();
        lastTrigger?.focus?.({ preventScroll: true });
      };

      cards.forEach(card => $('[data-rk4-reel-open]', card)?.addEventListener('click', event => {
        event.preventDefault();
        open(event.currentTarget);
      }));
      $('[data-rk4-viewer-close]', viewer)?.addEventListener('click', close);
      $('[data-rk4-viewer-prev]', viewer)?.addEventListener('click', () => render(index - 1));
      $('[data-rk4-viewer-next]', viewer)?.addEventListener('click', () => render(index + 1));
      viewer.addEventListener('cancel', event => { event.preventDefault(); close(); });
      viewer.addEventListener('click', event => { if (event.target === viewer) close(); });
      viewer.addEventListener('keydown', event => {
        if (event.key === 'ArrowLeft') render(index - 1);
        if (event.key === 'ArrowRight') render(index + 1);
      });
      let startX = null;
      let startY = null;
      viewer.addEventListener('pointerdown', event => {
        if (event.target.closest('a,button')) return;
        startX = event.clientX;
        startY = event.clientY;
      });
      viewer.addEventListener('pointerup', event => {
        if (startX === null) return;
        const dx = event.clientX - startX;
        const dy = event.clientY - startY;
        startX = null;
        startY = null;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) render(index + (dx < 0 ? 1 : -1));
      });
    });
  };

  const initCollection = root => {
    $$('[data-rk4-collection]', root).forEach(section => {
      if (section.dataset.rk4Ready === 'true') return;
      section.dataset.rk4Ready = 'true';
      const filters = $$('[data-rk4-filter]', section);
      filters.forEach(filter => filter.addEventListener('toggle', () => {
        if (!filter.open) return;
        filters.forEach(other => { if (other !== filter) other.open = false; });
      }));
      document.addEventListener('click', event => {
        if (event.target.closest('[data-rk4-filter]')) return;
        filters.forEach(filter => { filter.open = false; });
      });
    });
  };

  const initProduct = root => {
    $$('[data-rk4-product]', root).forEach(section => {
      if (section.dataset.rk4Ready === 'true') return;
      section.dataset.rk4Ready = 'true';
      const variantsNode = $('[data-rk4-variants]', section);
      let variants = [];
      try { variants = JSON.parse(variantsNode?.textContent || '[]'); } catch { variants = []; }

      const slides = $$('[data-rk4-slide]', section);
      const thumbs = $$('[data-rk4-thumb]', section);
      let slideIndex = Math.max(0, slides.findIndex(slide => slide.classList.contains('is-active')));
      const showSlide = next => {
        if (!slides.length) return;
        slideIndex = (next + slides.length) % slides.length;
        slides.forEach((slide, index) => {
          const active = index === slideIndex;
          slide.classList.toggle('is-active', active);
          slide.hidden = !active;
          if (!active) slide.querySelector('video')?.pause?.();
        });
        thumbs.forEach((thumb, index) => thumb.classList.toggle('is-active', index === slideIndex));
        const current = $('[data-rk4-gallery-current]', section);
        if (current) current.textContent = String(slideIndex + 1);
      };
      thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => showSlide(index)));
      $('[data-rk4-gallery-prev]', section)?.addEventListener('click', () => showSlide(slideIndex - 1));
      $('[data-rk4-gallery-next]', section)?.addEventListener('click', () => showSlide(slideIndex + 1));
      $('[data-rk4-stage]', section)?.addEventListener('keydown', event => {
        if (event.key === 'ArrowLeft') showSlide(slideIndex - 1);
        if (event.key === 'ArrowRight') showSlide(slideIndex + 1);
      });

      const groups = $$('[data-rk4-option]', section);
      const selected = groups.map(group => $('[aria-pressed="true"]', group)?.dataset.rk4Value || '');
      const variantInput = $('[data-rk4-variant-id]', section);
      const price = $('[data-rk4-price]', section);
      const compare = $('[data-rk4-compare]', section);
      const stock = $('[data-rk4-stock]', section);
      const addButton = $('[data-rk4-add]', section);
      const addLabel = $('[data-rk4-add-label]', section);
      const addPrice = $('[data-rk4-add-price]', section);
      const buyButton = $('[data-rk4-buy-now]', section);
      const mobilePrice = $('[data-rk4-mobile-price]', section);
      const mobileAdd = $('[data-rk4-mobile-add]', section);
      const mobileBuy = $('[data-rk4-mobile-buy]', section);

      const exactVariant = () => variants.find(variant => variant.options.every((value, index) => value === selected[index]));
      const valueAvailable = (position, value) => variants.some(variant =>
        variant.available && variant.options[position] === value &&
        (groups[position].dataset.optionKind === 'color' || variant.options.every((option, index) => index === position || option === selected[index]))
      );
      const updateOptionAvailability = () => groups.forEach((group, position) => {
        $$('[data-rk4-value]', group).forEach(button => { button.disabled = !valueAvailable(position, button.dataset.rk4Value || ''); });
      });
      const hydrate = variant => {
        const available = Boolean(variant?.available);
        if (variantInput) variantInput.value = variant?.id || '';
        if (price) price.textContent = variant?.priceFormatted || '';
        if (compare) {
          compare.textContent = variant?.compareFormatted || '';
          compare.hidden = !variant?.compareFormatted;
        }
        if (stock) { stock.textContent = available ? 'STOKTA • GÖNDERİME HAZIR' : 'TÜKENDİ'; stock.classList.toggle('is-sold-out', !available); }
        if (addButton) addButton.disabled = !available;
        if (buyButton) buyButton.disabled = !available;
        if (addLabel) addLabel.textContent = available ? 'SEPETE EKLE' : 'TÜKENDİ';
        if (addPrice) addPrice.textContent = variant?.priceFormatted || '';
        if (mobilePrice) mobilePrice.textContent = variant?.priceFormatted || '';
        if (mobileAdd) { mobileAdd.disabled = !available; mobileAdd.textContent = available ? 'SEPETE EKLE' : 'TÜKENDİ'; }
        if (mobileBuy) { mobileBuy.disabled = !available; mobileBuy.textContent = available ? 'SATIN AL' : 'TÜKENDİ'; }
        if (variant?.mediaId) {
          const mediaIndex = slides.findIndex(slide => String(slide.dataset.mediaId) === String(variant.mediaId));
          if (mediaIndex >= 0) showSlide(mediaIndex);
        }
        if (variant?.id) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          history.replaceState({}, '', url);
        }
        section.dispatchEvent(new CustomEvent('rocky:variant-change'));
      };

      groups.forEach((group, position) => {
        $$('[data-rk4-value]', group).forEach(button => button.addEventListener('click', () => {
          if (button.disabled) return;
          selected[position] = button.dataset.rk4Value || '';
          let variant = exactVariant();
          if (group.dataset.optionKind === 'color' && (!variant || !variant.available)) {
            variant = variants.find(item => item.available && item.options[position] === selected[position]) || variant;
            if (variant) variant.options.forEach((value, index) => { selected[index] = value; });
          }
          groups.forEach((optionGroup, optionIndex) => {
            $$('[data-rk4-value]', optionGroup).forEach(item => item.setAttribute('aria-pressed', String(item.dataset.rk4Value === selected[optionIndex])));
            const label = $('[data-rk4-selected]', optionGroup);
            if (label) label.textContent = selected[optionIndex];
          });
          updateOptionAvailability();
          hydrate(variant || exactVariant());
        }));
      });
      updateOptionAvailability();
      hydrate(exactVariant() || variants.find(variant => String(variant.id) === String(variantInput?.value)) || variants[0]);

      const quantity = $('input[name="quantity"]', section);
      $('[data-rk4-qty="minus"]', section)?.addEventListener('click', () => { if (quantity) quantity.value = String(Math.max(1, Number(quantity.value || 1) - 1)); });
      $('[data-rk4-qty="plus"]', section)?.addEventListener('click', () => { if (quantity) quantity.value = String(Math.max(1, Number(quantity.value || 1) + 1)); });

      const buyNow = async trigger => {
        const id = Number(variantInput?.value || 0);
        const qty = Math.max(1, Number(quantity?.value || 1));
        if (!id || trigger.disabled) return;
        const original = trigger.textContent;
        trigger.disabled = true;
        trigger.textContent = 'ÖDEMEYE HAZIRLANIYOR…';
        try {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id, quantity: qty }] })
          });
          if (!response.ok) throw new Error('buy-now');
          window.location.assign(`${window.Shopify?.routes?.root || '/'}checkout`);
        } catch {
          showToast('ÖDEME ADIMI AÇILAMADI. LÜTFEN TEKRAR DENE.');
          trigger.disabled = false;
          trigger.textContent = original;
        }
      };
      buyButton?.addEventListener('click', () => buyNow(buyButton));
      mobileAdd?.addEventListener('click', () => addButton?.click());
      mobileBuy?.addEventListener('click', () => buyNow(mobileBuy));

      const mobileBar = $('[data-rk4-mobile-atc]', section);
      const buyRow = $('[data-rk4-buy-row]', section);
      if (mobileBar && buyRow && 'IntersectionObserver' in window) {
        new IntersectionObserver(entries => mobileBar.classList.toggle('is-visible', !entries[0].isIntersecting), { threshold: .12 }).observe(buyRow);
      }
    });
  };

  const openCart = () => {
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  };
  document.addEventListener('cart:update', event => {
    if (event.detail?.data?.didError) return;
    // Quantity changes / removals inside the cart also dispatch cart:update — not an "added" event.
    if (event.detail?.data?.source === 'cart-items-component') return;
    showToast('ÜRÜN SEPETE EKLENDİ');
    window.setTimeout(openCart, 80);
  });

  const initialize = (root = document) => {
    initHeader(root);
    initMobileMenu(root);
    initTabs(root);
    initReveal(root);
    initVideos(root);
    initViewer(root);
    initCollection(root);
    initProduct(root);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:reorder', () => initReveal(document));
})();
